import React, { FC, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import DatePickerCustomHeaderTwoMonth from "../DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "../DatePickerCustomDay";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import fr from "date-fns/locale/fr";
import { getAuthToken } from "utils/storage";

registerLocale("fr", fr);
setDefaultLocale("fr");

// Helper function to format date (avoiding timezone issues)
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // Returns YYYY-MM-DD format in local time
};

// Helper function to parse date string and format for display (avoiding timezone issues)
const parseAndFormatDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString("fr-FR");
};

interface AvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  locationTitle: string;
  onSave?: () => void; // Callback pour rafraîchir les données
}

const AvailabilityModal: FC<AvailabilityModalProps> = ({ isOpen, onClose, locationId, locationTitle, onSave }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedRange, setSelectedRange] = useState<[Date | null, Date | null]>([null, null]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionMode, setActionMode] = useState<"block" | "unblock">("block");
  const [selectionMode, setSelectionMode] = useState<"individual" | "range">("range");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const handleDateClick = (date: Date) => {
    if (selectionMode === "individual") {
      const dateStr = formatDate(date);
      const isBlocked = blockedDates.includes(dateStr);

      if (isBlocked) {
        // Unblock the date
        setBlockedDates((prev) => prev.filter((d) => d !== dateStr));
        setActionMode("unblock");
      } else {
        // Block the date
        setBlockedDates((prev) => [...prev, dateStr]);
        setActionMode("block");
      }

      setSelectedDate(date);
    }
  };

  const handleRangeChange = (dates: [Date | null, Date | null]) => {
    setSelectedRange(dates);
    if (dates[0] && dates[1]) {
      // Generate all dates in the range
      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[1]);
      const rangeDates: string[] = [];

      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        rangeDates.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Check if all dates in range are blocked
      const allBlocked = rangeDates.every((dateStr) => blockedDates.includes(dateStr));

      if (allBlocked) {
        // Unblock all dates in range
        setBlockedDates((prev) => prev.filter((d) => !rangeDates.includes(d)));
        setActionMode("unblock");
      } else {
        // Block all dates in range
        setBlockedDates((prev) => {
          const newDates = [...prev];
          rangeDates.forEach((date) => {
            if (!newDates.includes(date)) {
              newDates.push(date);
            }
          });
          return newDates;
        });
        setActionMode("block");
      }
    }
  };

  const handleSave = async () => {
    if (blockedDates.length === 0) {
      onClose();
      return;
    }

    setLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      console.log("Saving blocked dates for location:", locationId, {
        blockedDates: blockedDates,
      });

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/unavailable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          locationId: locationId,
          // Backend expects startDay and endDay format
          startDay: blockedDates.length > 0 ? [...blockedDates].sort()[0] : null,
          endDay: blockedDates.length > 0 ? [...blockedDates].sort()[blockedDates.length - 1] : null,
        }),
      });

      if (response.ok) {
        console.log("Blocked dates saved successfully");
        setSaveSuccess(true);

        // Notifier le parent pour rafraîchir les données
        if (onSave) {
          onSave();
        }

        setTimeout(() => {
          onClose();
        }, 1500); // Delay pour montrer le message de succès
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Erreur ${response.status}: ${response.statusText}`;
        setSaveError(errorMessage);
        console.error("Failed to save blocked dates:", errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Erreur de connexion";
      setSaveError(errorMessage);
      console.error("Error saving blocked dates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearDates = () => {
    setSelectedDate(null);
    setSelectedRange([null, null]);
    setBlockedDates([]);
  };

  useEffect(() => {
    if (isOpen) {
      // Reset dates and messages when modal opens
      setSelectedDate(null);
      setSelectedRange([null, null]);
      setSaveError(null);
      setSaveSuccess(false);

      // Fetch existing blocked dates for this location
      fetchBlockedDates();
    }
  }, [isOpen, locationId]);

  const fetchBlockedDates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/unavailable/${locationId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Adapter selon la structure de réponse de l'API unavailable
        const unavailableDates = data.map((item: any) => item.date || item.unavailableDate).filter(Boolean);
        setBlockedDates(unavailableDates);
      } else {
        console.error("Failed to fetch blocked dates");
        setBlockedDates([]);
      }
    } catch (error) {
      console.error("Error fetching blocked dates:", error);
      setBlockedDates([]);
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <CalendarIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                      >
                        Gérer les disponibilités
                      </Dialog.Title>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{locationTitle}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-400" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-6">
                    <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                      Gérer les disponibilités
                    </h4>

                    {/* Selection Mode Toggle */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Mode de sélection:
                        </label>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectionMode("range");
                              setSelectedDate(null);
                              setSelectedRange([null, null]);
                            }}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                              selectionMode === "range"
                                ? "bg-blue-600 text-white shadow-md"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            📅 Plage de dates (recommandé)
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectionMode("individual");
                              setSelectedDate(null);
                              setSelectedRange([null, null]);
                            }}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                              selectionMode === "individual"
                                ? "bg-blue-600 text-white shadow-md"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            📆 Date individuelle
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectionMode === "individual"
                          ? "Cliquez sur une date pour la bloquer (rouge) ou la débloquer (vert)."
                          : "Cliquez sur la date de début puis sur la date de fin pour sélectionner une plage à bloquer ou débloquer."}
                      </p>
                      <div className="flex items-center space-x-4 text-xs">
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 bg-red-400 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-300">Dates bloquées</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 bg-green-400 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-300">Dates disponibles</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calendar */}
                  <div className="flex justify-center">
                    <div className="bg-gray-50 dark:bg-gray-900/20 rounded-xl p-4">
                      <DatePicker
                        locale="fr"
                        selected={selectionMode === "individual" ? selectedDate : selectedRange[0]}
                        onChange={(date: Date | null, event?: React.SyntheticEvent<any>) => {
                          if (selectionMode === "individual") {
                            if (date) {
                              handleDateClick(date);
                            }
                          } else {
                            // Range selection mode - this will be handled by selectsRange
                          }
                        }}
                        startDate={selectionMode === "range" ? selectedRange[0] : undefined}
                        endDate={selectionMode === "range" ? selectedRange[1] : undefined}
                        selectsRange={selectionMode === "range"}
                        onSelect={(date: Date | null) => {
                          if (selectionMode === "range") {
                            if (!selectedRange[0] || (selectedRange[0] && selectedRange[1])) {
                              // Start new range
                              setSelectedRange([date, null]);
                            } else {
                              // Complete the range
                              const newRange: [Date | null, Date | null] = [selectedRange[0], date];
                              if (newRange[0] && newRange[1] && newRange[0] > newRange[1]) {
                                // Swap if start is after end
                                newRange[0] = date;
                                newRange[1] = selectedRange[0];
                              }
                              handleRangeChange(newRange);
                            }
                          }
                        }}
                        monthsShown={2}
                        showPopperArrow={false}
                        inline
                        renderCustomHeader={(p) => <DatePickerCustomHeaderTwoMonth {...p} />}
                        renderDayContents={(day, date) => (
                          <DatePickerCustomDay
                            dayOfMonth={day}
                            date={date}
                            unavailableDays={blockedDates}
                            availabilityData={{
                              ...blockedDates.reduce((acc, blockedDate) => {
                                acc[blockedDate] = {
                                  hasSlots: false,
                                  isBlocked: true,
                                };
                                return acc;
                              }, {} as any),
                            }}
                          />
                        )}
                        minDate={new Date()}
                      />
                    </div>
                  </div>

                  {/* Range selection progress */}
                  {selectionMode === "range" && selectedRange[0] && !selectedRange[1] && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                        <span className="font-medium text-amber-800 dark:text-amber-200">
                          Date de début sélectionnée : {selectedRange[0].toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                      <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                        Cliquez maintenant sur la date de fin pour compléter votre sélection.
                      </p>
                    </motion.div>
                  )}

                  {/* Action summary display */}
                  {blockedDates.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800"
                    >
                      <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Dates actuellement bloquées :
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {blockedDates.map((date, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded text-xs"
                          >
                            {parseAndFormatDate(date)}
                          </span>
                        ))}
                      </div>
                      {(selectedDate || (selectedRange[0] && selectedRange[1])) && (
                        <p className="text-sm text-blue-600 dark:text-blue-300 mt-2">
                          {selectionMode === "individual" && selectedDate
                            ? actionMode === "block"
                              ? `✓ Date ${selectedDate.toLocaleDateString("fr-FR")} bloquée`
                              : `✓ Date ${selectedDate.toLocaleDateString("fr-FR")} débloquée`
                            : selectionMode === "range" && selectedRange[0] && selectedRange[1]
                              ? actionMode === "block"
                                ? `✓ Dates du ${selectedRange[0].toLocaleDateString("fr-FR")} au ${selectedRange[1].toLocaleDateString("fr-FR")} bloquées`
                                : `✓ Dates du ${selectedRange[0].toLocaleDateString("fr-FR")} au ${selectedRange[1].toLocaleDateString("fr-FR")} débloquées`
                              : null}
                        </p>
                      )}
                    </motion.div>
                  )}

                  {/* Error message */}
                  {saveError && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="font-medium text-red-800 dark:text-red-200">Erreur de sauvegarde</span>
                      </div>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1">{saveError}</p>
                    </motion.div>
                  )}

                  {/* Success message */}
                  {saveSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-medium text-green-800 dark:text-green-200">
                          ✅ Disponibilités sauvegardées avec succès !
                        </span>
                      </div>
                      <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                        Fermeture automatique dans quelques secondes...
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20">
                  <button
                    onClick={handleClearDates}
                    disabled={blockedDates.length === 0}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg"
                  >
                    🗑️ Tout débloquer ({blockedDates.length} dates)
                  </button>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Annuler
                    </button>
                    <ButtonPrimary onClick={handleSave} loading={loading} className="px-6 py-2 text-sm">
                      {loading ? "Enregistrement..." : "Sauvegarder"}
                    </ButtonPrimary>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AvailabilityModal;
